import { Show } from 'solid-js';
import { HeroCviImage } from './hero-cvi-types';
import { useHeroImageBuilder } from './image-helpers';

type CrossfadeImageProps = {
    /** Current image. Renders on top with a fade-in animation. */
    current: HeroCviImage | undefined;
    /** Previous image, shown layered underneath with a fade-out animation. Optional. */
    previous?: HeroCviImage | undefined;
    /** Width breakpoints for `srcset`. */
    srcsetWidths: readonly number[];
    /** `sizes` hint matching the rendered breakpoints. */
    sizes: string;
    /** Width used for the fallback `src` attribute (a sensible "default" pick). */
    fallbackWidth: number;
};

/**
 * Renders an image as a layered crossfade pair: the previous image fades out underneath
 * while the current image fades in on top. Both `<Show>` blocks are keyed on the image
 * reference so Solid remounts the `<img>` on slide change, which replays the CSS animation.
 *
 * The parent must supply `position: relative` and an `aspect-ratio` (or fixed height) so
 * the absolutely-positioned previous image has a box to fill.
 */
export const CrossfadeImage = (props: CrossfadeImageProps) => {
    const { buildImageSrc, buildImageSrcSet, imageStyle } = useHeroImageBuilder();

    return (
        <>
            {/* Previous — fading out, layered underneath. Decorative duplicate, hidden from ATs. */}
            <Show keyed when={props.previous}>
                {(prev) => (
                    <Show when={prev.url}>
                        <img
                            src={buildImageSrc(prev, props.fallbackWidth)}
                            srcset={buildImageSrcSet(prev, props.srcsetWidths)}
                            sizes={props.sizes}
                            alt=""
                            class="absolute inset-0 size-full object-cover animate-cvi-hero-slide-fade-out pointer-events-none"
                            style={imageStyle(prev)}
                            aria-hidden="true"
                        />
                    </Show>
                )}
            </Show>
            {/* Current — fading in on top. */}
            <Show keyed when={props.current}>
                {(curr) => (
                    <Show when={curr.url}>
                        <img
                            src={buildImageSrc(curr, props.fallbackWidth)}
                            srcset={buildImageSrcSet(curr, props.srcsetWidths)}
                            sizes={props.sizes}
                            alt={curr.altText ?? ''}
                            class="relative size-full object-cover animate-cvi-hero-slide-fade-in"
                            style={imageStyle(curr)}
                            {...!curr.altText ? { role: 'presentation' } : {}}
                        />
                    </Show>
                )}
            </Show>
        </>
    );
};
